import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { customizationProjectListDefaultParams } from "../config";
import {
  fetchCustomizationProjectDemandVersionOverviews,
  fetchCustomizationProjectItineraries,
  fetchCustomizationProjectList,
  fetchCustomizationProjectListForOpportunity,
  fetchMyDispatcherWorkload,
} from "../repo/customizationRepo";
import { normalizeCustomizationProjectListFilters } from "../service/customizationService";
import type {
  CustomizationMyWorkloadDetailType,
  CustomizationProjectListFilters,
  CustomizationProjectListItem,
  CustomizationProjectListParams,
  CustomizationProjectListScope,
  CustomizationProjectListSortBy,
  CustomizationProjectListSortOrder,
} from "../types";

const customizationProjectListQueryRootKey = ["customization", "projects"] as const;

type DemandVersionsDrawerContext = {
  projectId: string;
  projectName: string;
  projectCode?: string;
  legacyId?: string;
  operatingCompany?: string;
  salesUserName?: string;
  dispatcherUserName?: string;
  organizationNameSnapshot?: string;
  customerName?: string;
  contactName?: string;
  customerType?: string;
  tripType?: string;
  signType?: string;
  dealProgress?: string;
  projectCreatedAt?: string;
  /** 列表侧已知仅 1 个版本时，打开后直接展开详细。 */
  autoExpandDetail: boolean;
};

type DemandExcelDetailDrawerContext = Omit<DemandVersionsDrawerContext, "autoExpandDetail"> & {
  demandId: string;
  versionNumber?: string;
  versionSeq?: number;
};

type UseCustomizationProjectListRuntimeOptions = {
  createOnEntry?: boolean;
  opportunityId?: number;
  fixedFilters?: CustomizationProjectListFilters;
  fixedScope?: CustomizationProjectListScope;
  onCreated?: (projectId: string) => void;
  /** 单行程直开：先立刻展示加载中的预览抽屉。 */
  onBeginItineraryPreview?: () => void;
  onPreviewItinerary?: (itineraryId: string) => void;
  /** 单行程解析失败并回退列表时关闭加载预览。 */
  onAbortItineraryPreview?: () => void;
};

/** 管理定制项目列表查询、新增草稿与分页。 */
export function useCustomizationProjectListRuntime(
  options: UseCustomizationProjectListRuntimeOptions = {},
) {
  const [draftFilters, setDraftFilters] = useState<CustomizationProjectListFilters>({});
  const [submittedFilters, setSubmittedFilters] = useState<CustomizationProjectListFilters>({});
  const [page, setPage] = useState(customizationProjectListDefaultParams.page);
  const [pageSize, setPageSize] = useState(customizationProjectListDefaultParams.pageSize);
  const [scope, setScope] = useState<CustomizationProjectListScope>(
    customizationProjectListDefaultParams.scope,
  );
  const [explicitSort, setExplicitSort] = useState<{
    sortBy: CustomizationProjectListSortBy;
    sortOrder: CustomizationProjectListSortOrder;
  } | null>(null);
  const [itineraryDrawerPage, setItineraryDrawerPage] = useState(1);
  const [myWorkloadTab, setMyWorkloadTab] =
    useState<CustomizationMyWorkloadDetailType>("pending");
  const [drawer, setDrawer] = useState<
    | ({ type: "demand_versions" } & DemandVersionsDrawerContext)
    | ({ type: "demand_excel_detail" } & DemandExcelDetailDrawerContext)
    | { type: "itineraries"; projectId: string; projectName: string }
    | { type: "my_workload" }
    | null
  >(null);
  const [mutationErrorMessage, setMutationErrorMessage] = useState("");
  const createOnEntryTriggeredRef = useRef(false);
  const draftFiltersRef = useRef(draftFilters);
  draftFiltersRef.current = draftFilters;
  const onPreviewItineraryRef = useRef(options.onPreviewItinerary);
  onPreviewItineraryRef.current = options.onPreviewItinerary;
  const onBeginItineraryPreviewRef = useRef(options.onBeginItineraryPreview);
  onBeginItineraryPreviewRef.current = options.onBeginItineraryPreview;
  const onAbortItineraryPreviewRef = useRef(options.onAbortItineraryPreview);
  onAbortItineraryPreviewRef.current = options.onAbortItineraryPreview;
  const fixedFilters = useMemo(
    () => normalizeCustomizationProjectListFilters(options.fixedFilters ?? {}),
    [options.fixedFilters],
  );

  const listParams: CustomizationProjectListParams = useMemo(
    () => ({
      ...customizationProjectListDefaultParams,
      ...submittedFilters,
      ...fixedFilters,
      page,
      pageSize,
      scope: options.fixedScope ?? scope,
      ...(explicitSort ?? {}),
    }),
    [explicitSort, fixedFilters, options.fixedScope, page, pageSize, scope, submittedFilters],
  );

  const query = useQuery({
    queryKey: [...customizationProjectListQueryRootKey, listParams],
    queryFn: () =>
      options.opportunityId !== undefined
        ? fetchCustomizationProjectListForOpportunity(options.opportunityId, {
            page: listParams.page,
            pageSize: listParams.pageSize,
            keyword: listParams.keyword,
          })
        : fetchCustomizationProjectList(listParams),
  });

  const listPage = query.data ?? {
    items: [],
    total: 0,
    page,
    pageSize,
    viewerRole: "sales" as const,
    sortBy: "project_created_at" as const,
    sortOrder: "desc" as const,
  };

  const demandVersionsQuery = useQuery({
    queryKey: [
      ...customizationProjectListQueryRootKey,
      "demand-version-overviews",
      drawer?.type === "demand_versions" ? drawer.projectId : null,
    ],
    queryFn: () => fetchCustomizationProjectDemandVersionOverviews(drawer?.projectId ?? ""),
    enabled: drawer?.type === "demand_versions",
  });
  const itinerariesQuery = useQuery({
    queryKey: [
      ...customizationProjectListQueryRootKey,
      "itineraries",
      drawer?.type === "itineraries" ? drawer.projectId : null,
      itineraryDrawerPage,
    ],
    queryFn: () =>
      fetchCustomizationProjectItineraries(drawer?.projectId ?? "", itineraryDrawerPage),
    enabled: drawer?.type === "itineraries",
  });
  const myWorkloadQuery = useQuery({
    queryKey: [
      ...customizationProjectListQueryRootKey,
      "my-dispatcher-workload",
      myWorkloadTab,
    ],
    queryFn: () => fetchMyDispatcherWorkload(myWorkloadTab),
    enabled: drawer?.type === "my_workload",
  });

  useEffect(() => {
    if (!options.createOnEntry || createOnEntryTriggeredRef.current) {
      return;
    }
    createOnEntryTriggeredRef.current = true;
    setMutationErrorMessage("");
    options.onCreated?.("new");
  }, [options.createOnEntry, options.onCreated]);

  return {
    draftFilters,
    items: listPage.items,
    loading: query.isLoading || query.isFetching,
    creating: false,
    page,
    pageSize,
    total: listPage.total,
    viewerRole: listPage.viewerRole,
    sortBy: listPage.sortBy,
    sortOrder: listPage.sortOrder,
    scope,
    drawer,
    itineraryDrawerPage,
    itineraryPage: itinerariesQuery.data ?? null,
    demandVersions: demandVersionsQuery.data?.items ?? [],
    itineraries: itinerariesQuery.data?.items ?? [],
    myWorkloadTab,
    myWorkload: myWorkloadQuery.data ?? {
      detailType: myWorkloadTab,
      pendingOrderCount: 0,
      followingOrderCount: 0,
      unfinishedGroupCount: 0,
      finishedGroupCount: 0,
      orderItems: [],
      groupItems: [],
    },
    drawerLoading:
      drawer?.type === "demand_versions"
        ? demandVersionsQuery.isFetching
        : drawer?.type === "itineraries"
          ? itinerariesQuery.isFetching
          : drawer?.type === "my_workload"
            ? myWorkloadQuery.isFetching
            : false,
    drawerErrorMessage:
      (drawer?.type === "demand_versions" && demandVersionsQuery.error) ||
      (drawer?.type === "itineraries" && itinerariesQuery.error) ||
      (drawer?.type === "my_workload" && myWorkloadQuery.error)
        ? "详情加载失败，请稍后重试。"
        : "",
    errorMessage: query.error ? "定制项目列表加载失败，请稍后重试。" : "",
    mutationErrorMessage,
    setDraftFilters,
    handleSearch() {
      setSubmittedFilters(normalizeCustomizationProjectListFilters(draftFiltersRef.current));
      setPage(1);
    },
    handleReset() {
      setDraftFilters({});
      setSubmittedFilters({});
      setPage(1);
    },
    handlePageChange(nextPage: number) {
      setPage(Math.max(nextPage, 1));
    },
    handlePageSizeChange(nextPageSize: number) {
      setPageSize(nextPageSize);
      setPage(1);
    },
    handleScopeChange(nextScope: CustomizationProjectListScope) {
      setScope(nextScope);
      setPage(1);
    },
    handleSort(
      sortBy: CustomizationProjectListSortBy,
      sortOrder: CustomizationProjectListSortOrder,
    ) {
      setExplicitSort({ sortBy, sortOrder });
      setPage(1);
    },
    /**
     * 对齐旧系统 MySelfDo：始终打开版本概览抽屉，不进入工作台。
     * 仅 1 个版本时直接展开详细；多个版本时需点「详细」再看。
     */
    openDemandVersions(item: CustomizationProjectListItem) {
      const versionCount = item.demandVersionCount ?? 0;
      if (versionCount <= 0) {
        return;
      }
      setItineraryDrawerPage(1);
      setDrawer({
        type: "demand_versions",
        projectId: item.id,
        projectName: item.name,
        projectCode: item.projectCode || item.legacyId,
        legacyId: item.legacyId,
        operatingCompany: item.operatingCompany,
        salesUserName: item.salesUserName,
        dispatcherUserName: item.dispatcherUserName,
        organizationNameSnapshot: item.organizationNameSnapshot,
        customerName: item.customerName,
        contactName: item.contactName,
        customerType: item.customerType,
        tripType: item.tripType,
        signType: item.signType,
        dealProgress: item.dealProgress,
        projectCreatedAt: item.projectCreatedAt,
        autoExpandDetail: versionCount === 1,
      });
    },
    /** 打开「我的未接单」抽屉，默认 Tab 为未接单。 */
    openMyWorkload() {
      setMyWorkloadTab("pending");
      setDrawer({ type: "my_workload" });
    },
    handleMyWorkloadTabChange(nextTab: CustomizationMyWorkloadDetailType) {
      setMyWorkloadTab(nextTab);
    },
    /** 列表「当前版本」：右侧抽屉直接展示该有效版本的 Excel 详表。 */
    openDemandExcelDetail(
      item: Pick<
        CustomizationProjectListItem,
        | "id"
        | "name"
        | "projectCode"
        | "legacyId"
        | "operatingCompany"
        | "salesUserName"
        | "dispatcherUserName"
        | "organizationNameSnapshot"
        | "customerName"
        | "contactName"
        | "customerType"
        | "tripType"
        | "signType"
        | "dealProgress"
        | "projectCreatedAt"
        | "effectiveDemandId"
        | "versionNumber"
        | "versionSeq"
      >,
    ) {
      const demandId = item.effectiveDemandId?.trim();
      if (!demandId) {
        return;
      }
      setDrawer({
        type: "demand_excel_detail",
        projectId: item.id,
        projectName: item.name,
        projectCode: item.projectCode || item.legacyId,
        legacyId: item.legacyId,
        operatingCompany: item.operatingCompany,
        salesUserName: item.salesUserName,
        dispatcherUserName: item.dispatcherUserName,
        organizationNameSnapshot: item.organizationNameSnapshot,
        customerName: item.customerName,
        contactName: item.contactName,
        customerType: item.customerType,
        tripType: item.tripType,
        signType: item.signType,
        dealProgress: item.dealProgress,
        projectCreatedAt: item.projectCreatedAt,
        demandId,
        versionNumber: item.versionNumber,
        versionSeq: item.versionSeq,
      });
    },
    openItineraries(item: Pick<CustomizationProjectListItem, "id" | "name" | "itineraryCount">) {
      const itineraryCount = item.itineraryCount ?? 0;
      if (itineraryCount <= 0) {
        return;
      }
      if (itineraryCount === 1) {
        onBeginItineraryPreviewRef.current?.();
        void (async () => {
          try {
            const page = await fetchCustomizationProjectItineraries(item.id, 1);
            const onlyItineraryId = page.items[0]?.id?.trim();
            if (onlyItineraryId) {
              onPreviewItineraryRef.current?.(onlyItineraryId);
              return;
            }
          } catch {
            // 单行程直开失败时回退到列表抽屉，便于继续排查。
          }
          onAbortItineraryPreviewRef.current?.();
          setItineraryDrawerPage(1);
          setDrawer({ type: "itineraries", projectId: item.id, projectName: item.name });
        })();
        return;
      }
      setItineraryDrawerPage(1);
      setDrawer({ type: "itineraries", projectId: item.id, projectName: item.name });
    },
    openLatestItinerary(item: Pick<CustomizationProjectListItem, "latestItineraryId">) {
      const itineraryId = item.latestItineraryId?.trim();
      if (!itineraryId) {
        return;
      }
      onBeginItineraryPreviewRef.current?.();
      onPreviewItineraryRef.current?.(itineraryId);
    },
    handleItineraryDrawerPageChange(nextPage: number) {
      setItineraryDrawerPage(Math.max(nextPage, 1));
    },
    closeDrawer() {
      setDrawer(null);
    },
    handleCreateProject() {
      setMutationErrorMessage("");
      options.onCreated?.("new");
    },
  };
}
